import "@fontsource/inter/index.css";
import "@fontsource/montserrat/index.css";
import { Badge, Button, Card, Container, Grid, Group, Stack, Text, Title } from "@mantine/core";
import {
  IconArrowRight,
  IconBook,
  IconGlobe,
  IconHeart,
  IconMapPin,
  IconSchool,
  IconTarget,
  IconUsers,
  IconWorld,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AllFatherContext } from "../../App";

import LanguageSelector from "../../Lib/i18n/LanguageSelector";

import "./styles.scss";

const About: React.FC = () => {
  const { t } = useTranslation();
  const context = useContext(AllFatherContext);

  // Enforce light mode for about page
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    if (context && context.setTheme) {
      context.setTheme("light");
    }
    localStorage.setItem("app-theme", "light");
  }, []);

  const regions = [
    { name: "United Kingdom", icon: IconMapPin },
    { name: "Canada", icon: IconMapPin },
    { name: "United States", icon: IconMapPin },
    { name: "Ireland", icon: IconMapPin },
    { name: "Malta", icon: IconMapPin },
    { name: "Nigeria", icon: IconMapPin },
    { name: "Sweden", icon: IconMapPin },
    { name: "Netherlands", icon: IconMapPin },
    { name: "Germany", icon: IconMapPin },
    { name: "Norway", icon: IconMapPin },
    { name: "Denmark", icon: IconMapPin },
  ];

  const educationLevels = [
    {
      title: "Key Stages 1–4",
      region: "UK",
      description: "Comprehensive coverage of the British curriculum",
    },
    {
      title: "Grades K–12",
      region: "US and Canada",
      description: "Complete American and Canadian educational standards",
    },
    {
      title: "Junior and Senior Cycle",
      region: "Ireland",
      description: "Irish educational system support",
    },
    {
      title: "International Schools",
      region: "Europe",
      description: "Global curriculum alignment",
    },
  ];

  const subjects = [
    {
      title: "Mathematics",
      topics: ["Arithmetic", "Algebra", "Geometry", "Statistics"],
      icon: IconBook,
      gradient: "linear-gradient(135deg, #a78bfa 0%, #6366f1 100%)",
    },
    {
      title: "English Language & Literature",
      topics: ["Reading Comprehension", "Grammar", "Writing", "Literary Analysis"],
      icon: IconBook,
      gradient: "linear-gradient(135deg, #f472b6 0%, #ec4899 100%)",
    },
    {
      title: "Sciences",
      topics: ["Biology", "Chemistry", "Physics"],
      icon: IconSchool,
      gradient: "linear-gradient(135deg, #34d399 0%, #10b981 100%)",
    },
  ];

  const values = [
    {
      title: "Academic Excellence",
      description: "Rigorous standards and proven methodologies",
      icon: IconTarget,
    },
    {
      title: "Personalized Learning",
      description: "Tailored approaches for individual student needs",
      icon: IconUsers,
    },
    {
      title: "Global Accessibility",
      description: "World-class education available anywhere",
      icon: IconGlobe,
    },
    {
      title: "Passionate Educators",
      description: "Experienced tutors dedicated to student success",
      icon: IconHeart,
    },
  ];

  return (
    <div className="about-container">
      {/* Header */}
      <header className="about-header">
        <Container size="xl">
          <Group justify="space-between" align="center" h={80}>
            <Link to="/">
              <img className="logo" src={context?.assets?.logos?.legacy} alt="EmRil Academy Logo" />
            </Link>
            <Group gap="md">
              <LanguageSelector />
              <Link to="/">
                <Button
                  variant="outline"
                  radius="xl"
                  size="sm"
                  className="nav-btn nav-btn--green-outline"
                >
                  Back to Home
                </Button>
              </Link>
            </Group>
          </Group>
        </Container>
      </header>

      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-bg-dots">
          <div className="about-bg-dot about-bg-dot1" />
          <div className="about-bg-dot about-bg-dot2" />
          <div className="about-bg-dot about-bg-dot3" />
          <div className="about-bg-dot about-bg-dot4" />
          <div className="about-bg-dot about-bg-dot5" />
        </div>
        <Container size="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-content"
          >
            <Badge
              size="lg"
              variant="gradient"
              gradient={{ from: "violet", to: "blue", deg: 90 }}
              radius="xl"
              px={16}
              py={8}
              mb="xl"
              style={{ fontWeight: 700 }}
            >
              About Emdex Academy
            </Badge>
            <Title order={1} size="3.5rem" fw={900} className="hero-title" ta="center" mb="xl">
              Leading Online Tutoring Platform
            </Title>
            <Text size="xl" c="dimmed" ta="center" className="hero-subtitle" mb="xl">
              Dedicated to providing high-quality, personalized education to students across the
              globe
            </Text>
          </motion.div>
        </Container>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <Container size="xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <Text ta="center" size="2.5rem" fw={700} className="section-title" mb="md">
              Our Mission
            </Text>
            <Text ta="center" size="lg" c="dimmed" className="section-subtitle">
              To make academic excellence accessible, affordable, interactive, and inspiring for
              every learner.
            </Text>
          </motion.div>

          <Grid gutter={40} mt={60}>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="mission-card" radius="xl" shadow="xl" withBorder>
                  <Stack gap="lg">
                    <div className="card-icon">
                      <IconTarget size={40} color="#a78bfa" />
                    </div>
                    <Title order={3} fw={800} className="card-title">
                      Exceptional, Experienced Tutors
                    </Title>
                    <Text size="md" c="gray.3" lh={1.7}>
                      Our teachers undergo a rigorous selection process and bring years of proven
                      success in both traditional and online classrooms. Their ability to tailor
                      lessons to individual learning styles ensures every student receives the
                      focused support they need to thrive.
                    </Text>
                  </Stack>
                </Card>
              </motion.div>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <Card className="mission-card" radius="xl" shadow="xl" withBorder>
                  <Stack gap="lg">
                    <div className="card-icon">
                      <IconGlobe size={40} color="#6366f1" />
                    </div>
                    <Title order={3} fw={800} className="card-title">
                      Flexible, Engaging Learning
                    </Title>
                    <Text size="md" c="gray.3" lh={1.7}>
                      We provide a flexible, engaging, and fully remote learning experience,
                      empowering students to learn at their own pace, from the comfort of their
                      homes—anywhere in the world.
                    </Text>
                  </Stack>
                </Card>
              </motion.div>
            </Grid.Col>
          </Grid>
        </Container>
      </section>

      {/* Global Reach Section */}
      <section className="global-reach-section">
        <Container size="xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <Text ta="center" size="2.5rem" fw={700} className="section-title" mb="md">
              Global Reach
            </Text>
            <Text ta="center" size="lg" c="dimmed" className="section-subtitle">
              Serving students across key English-speaking regions of Europe and beyond
            </Text>
          </motion.div>

          <Grid gutter={20} mt={60}>
            {regions.map((region, index) => (
              <Grid.Col key={region.name} span={{ base: 6, sm: 4, md: 3, lg: 2 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <Card className="region-card" radius="lg" withBorder>
                    <Stack gap="sm" align="center" ta="center">
                      <div className="region-icon">
                        <region.icon size={24} color="#a78bfa" />
                      </div>
                      <Text size="sm" fw={600} c="white">
                        {region.name}
                      </Text>
                    </Stack>
                  </Card>
                </motion.div>
              </Grid.Col>
            ))}
          </Grid>
        </Container>
      </section>

      {/* Education Levels Section */}
      <section className="education-levels-section">
        <Container size="xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <Text ta="center" size="2.5rem" fw={700} className="section-title" mb="md">
              Education Levels
            </Text>
            <Text ta="center" size="lg" c="dimmed" className="section-subtitle">
              Supporting students across multiple education systems
            </Text>
          </motion.div>

          <Grid gutter={32} mt={60}>
            {educationLevels.map((level, index) => (
              <Grid.Col key={level.title} span={{ base: 12, sm: 6, lg: 3 }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="education-level-card" radius="xl" shadow="md" withBorder>
                    <Stack gap="md" align="center" ta="center">
                      <div className="level-icon">
                        <IconSchool size={32} color="#a78bfa" />
                      </div>
                      <Title order={4} fw={700} c="white">
                        {level.title}
                      </Title>
                      <Badge variant="light" color="violet" size="sm">
                        {level.region}
                      </Badge>
                      <Text size="sm" c="gray.3" ta="center">
                        {level.description}
                      </Text>
                    </Stack>
                  </Card>
                </motion.div>
              </Grid.Col>
            ))}
          </Grid>
        </Container>
      </section>

      {/* Subjects Section */}
      <section className="subjects-section">
        <Container size="xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <Text ta="center" size="2.5rem" fw={700} className="section-title" mb="md">
              Expert Instruction
            </Text>
            <Text ta="center" size="lg" c="dimmed" className="section-subtitle">
              Comprehensive coverage of core academic subjects
            </Text>
          </motion.div>

          <Grid gutter={32} mt={60}>
            {subjects.map((subject, index) => (
              <Grid.Col key={subject.title} span={{ base: 12, md: 4 }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card
                    className="subject-card"
                    radius="xl"
                    shadow="xl"
                    withBorder
                    style={{ background: subject.gradient }}
                  >
                    <Stack gap="lg" align="center" ta="center">
                      <div className="subject-icon">
                        <subject.icon size={40} color="white" />
                      </div>
                      <Title order={3} fw={800} c="white">
                        {subject.title}
                      </Title>
                      <Stack gap="xs">
                        {subject.topics.map((topic) => (
                          <Text key={topic} size="sm" c="white" fw={500}>
                            • {topic}
                          </Text>
                        ))}
                      </Stack>
                    </Stack>
                  </Card>
                </motion.div>
              </Grid.Col>
            ))}
          </Grid>
        </Container>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <Container size="xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="section-header"
          >
            <Text ta="center" size="2.5rem" fw={700} className="section-title" mb="md">
              Our Values
            </Text>
            <Text ta="center" size="lg" c="dimmed" className="section-subtitle">
              The principles that guide our educational approach
            </Text>
          </motion.div>

          <Grid gutter={32} mt={60}>
            {values.map((value, index) => (
              <Grid.Col key={value.title} span={{ base: 12, sm: 6, lg: 3 }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="value-card" radius="xl" shadow="md" withBorder>
                    <Stack gap="md" align="center" ta="center">
                      <div className="value-icon">
                        <value.icon size={32} color="#a78bfa" />
                      </div>
                      <Title order={4} fw={700} c="white">
                        {value.title}
                      </Title>
                      <Text size="sm" c="gray.3" ta="center">
                        {value.description}
                      </Text>
                    </Stack>
                  </Card>
                </motion.div>
              </Grid.Col>
            ))}
          </Grid>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Container size="xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <Card className="cta-card" radius="xl" shadow="xl" withBorder>
              <Stack gap="xl" align="center" ta="center">
                <Title order={2} fw={800} className="cta-title">
                  Ready to Start Your Learning Journey?
                </Title>
                <Text size="lg" c="gray.3" className="cta-subtitle">
                  Whether your child is building foundational knowledge, bridging academic gaps, or
                  striving for top results, we are here to guide them every step of the way.
                </Text>
                <Group gap="md">
                  <Link to="/plans">
                    <Button
                      size="lg"
                      radius="xl"
                      className="primary-btn"
                      style={{ fontWeight: 700 }}
                    >
                      Explore Our Plans
                      <IconArrowRight size={20} />
                    </Button>
                  </Link>
                  <Link to="/sign-up">
                    <Button variant="outline" size="lg" radius="xl" className="secondary-btn">
                      Get Started Today
                    </Button>
                  </Link>
                </Group>
              </Stack>
            </Card>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default About;
